import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  DollarSign,
  TrendingUp,
  Users as UsersIcon,
  Link2,
} from "lucide-react";
import ChatterlyLivePanel from "./ChatterlyLivePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Panel principal de Chatterly — muestra estado de conexión, lista de creators,
 * ventas diarias por creator y top spenders. Todo se pobla desde
 * /api/chatterly/* (con sync automático cada 6h + botón manual).
 */
export default function ChatterlyPanel() {
  const [status, setStatus] = useState(null);
  const [creators, setCreators] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sales, setSales] = useState(null);
  const [spenders, setSpenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastRuns, setLastRuns] = useState([]);
  const [subView, setSubView] = useState('sync'); // 'sync' | 'live'
  const [crmModels, setCrmModels] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const [s, sy] = await Promise.all([
        axios.get(`${API}/chatterly/status`, { withCredentials: true }),
        axios.get(`${API}/chatterly/sync/status`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setLastRuns(sy.data.last_runs || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadCreators = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chatterly/creators`, {
        withCredentials: true,
      });
      const list = r.data.creators || [];
      setCreators(list);
      if (!selected && list.length) {
        setSelected(list[0].creator_id || list[0].creatorID);
      }
    } catch (e) {
      console.error(e);
    }
  }, [selected]);

  const loadDetail = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const [salesRes, spendersRes] = await Promise.all([
        axios.get(`${API}/chatterly/models/${cid}/sales?days=30`, {
          withCredentials: true,
        }),
        axios.get(`${API}/chatterly/models/${cid}/spenders?limit=20`, {
          withCredentials: true,
        }),
      ]);
      setSales(salesRes.data);
      setSpenders(spendersRes.data.spenders || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadStatus();
      await loadCreators();
      // Cargar modelos del CRM para el dropdown de vinculación
      try {
        const mr = await axios.get(`${API}/models`, { withCredentials: true });
        setCrmModels(Array.isArray(mr.data) ? mr.data : mr.data.models || []);
      } catch (e) {
        setCrmModels([]);
      }
      setLoading(false);
    })();
  }, [loadStatus, loadCreators]);

  const handleLinkModelo = async (creatorId, modeloId) => {
    try {
      await axios.put(
        `${API}/chatterly/creators/${creatorId}/link`,
        { modelo_id: modeloId || null },
        { withCredentials: true }
      );
      toast.success(modeloId ? "Vinculada al CRM" : "Desvinculada");
      await loadCreators();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al vincular");
    }
  };

  useEffect(() => {
    if (selected) loadDetail(selected);
  }, [selected, loadDetail]);

  const handleSync = async () => {
    if (
      !window.confirm(
        "¿Sincronizar TODAS las creators con Chatterly ahora? Tarda 1-3 min según la cantidad de creators."
      )
    )
      return;
    setSyncing(true);
    try {
      await axios.post(`${API}/chatterly/sync/run`, {}, { withCredentials: true });
      toast.success("Sincronización lanzada — los datos se actualizarán en 1-3 min.");
      // Refrescar status en 20s
      setTimeout(async () => {
        await loadStatus();
        await loadCreators();
        if (selected) await loadDetail(selected);
        setSyncing(false);
      }, 20000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sincronización");
      setSyncing(false);
    }
  };

  const totalGross = sales?.totals?.gross || 0;
  const totalNet = sales?.totals?.net || 0;

  const rowsForChart = useMemo(() => {
    const rows = sales?.rows || [];
    if (!rows.length) return [];
    const max = Math.max(1, ...rows.map((r) => r.gross || 0));
    return rows.map((r) => ({ ...r, pct: ((r.gross || 0) / max) * 100 }));
  }, [sales]);

  const currentCreator = creators.find(
    (c) => (c.creator_id || c.creatorID) === selected
  );

  return (
    <div className="p-6 space-y-6" data-testid="chatterly-panel">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-white text-2xl font-bold">Chatterly</h2>
            <p className="text-sm text-gray-400">
              Sincronización de ventas y top spenders desde OnlyFans
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span
              data-testid="chatterly-status-pill"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                status.ok
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                  : "bg-red-500/10 border-red-500/30 text-red-300"
              }`}
            >
              {status.ok ? (
                <CheckCircle2 className="w-3.5 h-3.5" />
              ) : (
                <AlertCircle className="w-3.5 h-3.5" />
              )}
              {status.message}
            </span>
          )}
          <button
            onClick={handleSync}
            disabled={syncing || !status?.configured}
            data-testid="chatterly-sync-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`}
            />
            Sincronizar ahora
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="inline-flex bg-white/5 border border-white/10 rounded-xl p-1 mt-2">
        <button
          onClick={() => setSubView('sync')}
          data-testid="chatterly-subview-sync"
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
            subView === 'sync'
              ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          📊 Sincronización y ventas
        </button>
        <button
          onClick={() => setSubView('live')}
          data-testid="chatterly-subview-live"
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
            subView === 'live'
              ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          💬 Mensajería en vivo
        </button>
      </div>

      {subView === 'live' && status?.configured ? (
        <ChatterlyLivePanel />
      ) : subView === 'live' ? (
        <div className="p-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 text-amber-300 text-sm">
          Configura primero la API key para ver la mensajería en vivo.
        </div>
      ) : (
      <>
      {/* Missing key CTA */}
      {status && !status.configured && (
        <div className="p-5 rounded-2xl border border-amber-500/30 bg-amber-500/5">
          <h3 className="text-amber-300 font-semibold mb-1">
            Configura tu API key de Chatterly
          </h3>
          <p className="text-sm text-gray-300">
            En Chatterly → pestaña <strong>API</strong>, genera tu API key (empieza con{" "}
            <code className="text-fuchsia-300">chtly_...</code>) y pégala al agente en el chat con este
            formato: <code>CHATTERLY_API_KEY=tu_key_aqui</code>. Se guardará como variable de entorno
            y esta pantalla se activará.
          </p>
        </div>
      )}

      {/* Last runs */}
      {lastRuns.length > 0 && (
        <details className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-2 text-sm">
          <summary className="cursor-pointer text-gray-400 hover:text-white text-xs">
            Última sincronización: <span className="text-white">{new Date(lastRuns[0].started_at).toLocaleString("es-CO")}</span>{" "}
            · estado <span className="text-white">{lastRuns[0].status}</span> · creators sincronizados: <span className="text-white">{lastRuns[0].creators_synced}</span>
          </summary>
          <div className="mt-2 space-y-1">
            {lastRuns.map((run) => (
              <div key={run.id} className="text-xs text-gray-400 flex gap-3">
                <span>{new Date(run.started_at).toLocaleString("es-CO")}</span>
                <span
                  className={
                    run.status === "ok"
                      ? "text-emerald-400"
                      : run.status === "partial"
                      ? "text-amber-400"
                      : run.status === "failed"
                      ? "text-red-400"
                      : "text-gray-400"
                  }
                >
                  {run.status}
                </span>
                <span>{run.creators_synced} creators</span>
                {run.error && (
                  <span className="text-red-400 truncate">⚠ {run.error}</span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Main grid */}
      {loading ? (
        <div className="py-20 text-center text-gray-500">Cargando…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Creators sidebar */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-gray-400 mb-2 px-2">
              <UsersIcon className="w-3.5 h-3.5" />
              Creators ({creators.length})
            </div>
            {creators.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-6">
                Aún sin creators. Presiona &quot;Sincronizar ahora&quot;.
              </div>
            ) : (
              <div className="space-y-1 max-h-[70vh] overflow-y-auto">
                {creators.map((c) => {
                  const cid = c.creator_id || c.creatorID;
                  const isActive = cid === selected;
                  return (
                    <div
                      key={cid}
                      className={`rounded-lg text-sm transition ${
                        isActive
                          ? "bg-gradient-to-r from-emerald-500/20 to-teal-500/10 border border-emerald-500/30"
                          : "hover:bg-white/5 border border-transparent"
                      }`}
                    >
                      <button
                        onClick={() => setSelected(cid)}
                        data-testid={`chatterly-creator-${cid}`}
                        className="w-full text-left px-3 pt-2 pb-1"
                      >
                        <div className="font-medium truncate text-white">
                          {c.name || c.username || cid}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate">
                          @{c.username || "—"} {c.linked === false && "· sin conectar"}
                        </div>
                      </button>
                      <div className="px-2 pb-2 flex items-center gap-1">
                        <Link2 className="w-3 h-3 text-gray-500" />
                        <select
                          value={c.modelo_id || ""}
                          onChange={(e) => handleLinkModelo(cid, e.target.value)}
                          data-testid={`chatterly-link-modelo-${cid}`}
                          className="flex-1 text-[10px] bg-zinc-900 border border-white/10 rounded px-1 py-0.5 text-gray-300 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        >
                          <option value="">— sin vincular —</option>
                          {crmModels.map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </select>
                      </div>
                      {c.modelo && (
                        <div className="px-3 pb-2 text-[10px] text-emerald-400">
                          ↳ {c.modelo.name}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="space-y-5">
            {!currentCreator ? (
              <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-gray-500 text-sm">
                {creators.length === 0
                  ? "Sincroniza tus creators para empezar."
                  : "Selecciona una creator en la izquierda."}
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard
                    icon={DollarSign}
                    label="Ventas 30d (bruto)"
                    value={`$${totalGross.toLocaleString()}`}
                    accent="emerald"
                  />
                  <StatCard
                    icon={TrendingUp}
                    label="Neto 30d"
                    value={`$${totalNet.toLocaleString()}`}
                    accent="teal"
                  />
                  <StatCard
                    icon={DollarSign}
                    label="Tips 30d"
                    value={`$${(sales?.totals?.tips_gross || 0).toLocaleString()}`}
                    accent="fuchsia"
                  />
                  <StatCard
                    icon={DollarSign}
                    label="PPV chat 30d"
                    value={`$${(sales?.totals?.chat_messages_gross || 0).toLocaleString()}`}
                    accent="teal"
                  />
                </div>

                {/* Daily sales bar chart */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <h3 className="text-white font-semibold text-sm mb-1">
                    Snapshots diarios (total móvil 30 días)
                  </h3>
                  <p className="text-[11px] text-gray-500 mb-3">
                    Cada barra = un snapshot del total rolling-30d en esa fecha. Con más días de historial se ve el crecimiento.
                  </p>
                  {rowsForChart.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      Sin snapshots aún. El cron corre cada 6h — al día siguiente verás el primero.
                    </div>
                  ) : (
                    <>
                      <div className="flex items-end gap-1 h-32">
                        {rowsForChart.map((r) => (
                          <div
                            key={r.date}
                            className="flex-1 h-full flex flex-col items-center justify-end group relative"
                            title={`${r.date}: $${r.gross}`}
                          >
                            <div
                              className="w-full bg-gradient-to-t from-emerald-500 to-teal-400 rounded-t-sm transition-all"
                              style={{ height: `${r.pct}%` }}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 flex justify-between text-[10px] text-gray-500">
                        <span>{rowsForChart[0]?.date}</span>
                        <span>{rowsForChart[rowsForChart.length - 1]?.date}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Top spenders */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <h3 className="text-white font-semibold text-sm mb-1">
                    Fans con último gasto reciente
                  </h3>
                  <p className="text-[11px] text-gray-500 mb-3">
                    Muestra la cantidad del último gasto por fan (no el total histórico — Chatterly no expone acumulado).
                  </p>
                  {spenders.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      Sin datos aún. Espera al próximo sync.
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {[...spenders]
                        .sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0))
                        .map((s, idx) => (
                          <div
                            key={s.fan_id}
                            data-testid={`chatterly-spender-${s.fan_id}`}
                            className="flex items-center justify-between py-2"
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-6 text-center text-xs text-gray-500">
                                #{idx + 1}
                              </span>
                              {s.avatar ? (
                                <img
                                  src={s.avatar}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover"
                                  onError={(e) => (e.target.style.display = "none")}
                                />
                              ) : null}
                              <div>
                                <div className="text-white text-sm font-medium">
                                  {s.name || `@${s.username}` || "Anon"}
                                </div>
                                {s.username && (
                                  <div className="text-[11px] text-gray-500">
                                    @{s.username}
                                    {s.last_seen && ` · ${new Date(s.last_seen).toLocaleDateString('es-CO')}`}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-emerald-400 font-bold text-sm">
                              ${Number(s.total_spent || 0).toLocaleString()}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = "emerald" }) {
  const accents = {
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/20 text-emerald-300",
    teal: "from-teal-500/20 to-teal-500/5 border-teal-500/20 text-teal-300",
    fuchsia: "from-fuchsia-500/20 to-fuchsia-500/5 border-fuchsia-500/20 text-fuchsia-300",
  };
  return (
    <div
      className={`rounded-2xl bg-gradient-to-br border p-4 ${
        accents[accent] || accents.emerald
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-white text-xl font-bold">{value}</div>
    </div>
  );
}
